import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { customAPI } from "services/utils/CustomAPI";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import RequestStatusTimeline from "components/DesignSystem/RequestStatusTimeline";
import ApprovalEditAuditTrail from "components/DesignSystem/ApprovalEditAuditTrail";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { RequestCancelButton } from "components/DesignSystem/RequestCancelButton";
import {
  detailAccentPalette,
  detailCardSx,
  detailSectionLabelSx,
  RequestDetailField,
} from "components/DesignSystem/RequestDetailField";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import dayjs from "dayjs";
import {
  designColors,
  designShell,
} from "services/constants/designTokens";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { approvePanelUi, useGoToApprovalCenter } from "../approveUi";
import {
  checkFirstLevelApproverPermission,
  checkSecondLevelApproverPermission,
} from "services/utils/HelperFunction";
import { canonicalBreakStatus, breakStatusVisual } from "./BreakStatusCards";

const toISODate = (dateStr) => {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const parts = dateStr.split("-");
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return dateStr;
};

const formatBreakTableTime = (timeValue) => {
  if (!timeValue) return "-";
  const parsedTime = dayjs(timeValue);
  return parsedTime.isValid() ? parsedTime.format("HH:mm") : timeValue;
};

const detailAccent = {
  category: detailAccentPalette.blue,
  date: detailAccentPalette.indigo,
  startTime: detailAccentPalette.green,
  endTime: detailAccentPalette.orange,
  duration: detailAccentPalette.purple,
  reason: detailAccentPalette.orange,
  rejectReason: detailAccentPalette.purple,
};

export const BreakDetailPanel = ({
  open,
  onClose,
  breakRequest,
  checkAccess,
  setReCallApi,
  expanded = false,
  onToggleExpand,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const goToApprovalCenter = useGoToApprovalCenter();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  // No dedicated cancel-an-approved-break endpoint exists yet, same gap as
  // Comp Off (see CompOffDetailPanel) — this reuses the same approve/reject
  // endpoint below with status: "Cancelled" as a first attempt, needs
  // verifying against the real backend.
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState("");
  const { domain, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  // See Leave/LeaveDetailPanel.js — Approve/Reject/Cancel used to be gated
  // by plain editor permission (checkAccess). Now requires real approver
  // permission (either level counts).
  const hasApproverAccess =
    checkFirstLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Break",
    ) ||
    checkSecondLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Break",
    );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: { rejected_reason: "" },
  });

  useEffect(() => {
    setRejectDialogOpen(false);
    setCancelDialogOpen(false);
    setCancelReason("");
    setCancelReasonError("");
    reset({ rejected_reason: "" });
  }, [breakRequest, reset]);

  useEffect(() => {
    register("rejected_reason", {
      required: "Reject reason is required",
      validate: {
        minLengthNoSpaces: (value) => {
          if (value?.trim().length < 3) return minimum3Characters;
          if (value?.trim().length > 255) return "Maximum length is 255 characters";
          return true;
        },
        atleastOneCharacterWithSpaceReg: (value) =>
          atleastOneCharacterWithSpace.test(value) || "Atleast one alphabet required",
      },
    });
  }, [register]);

  const statusFlowData = useMemo(
    () => ({
      ...(breakRequest || {}),
      applied_by:
        breakRequest?.applied_by ||
        breakRequest?.employee_name ||
        breakRequest?.username ||
        breakRequest?.requested_by,
      applied_on:
        breakRequest?.applied_on ||
        breakRequest?.applied_date ||
        breakRequest?.created_at ||
        breakRequest?.created_date ||
        breakRequest?.date,
      // breakRequest?.approver_name is the assigned approver, stamped at
      // apply time before anyone has acted -- it is not proof of approval
      // and must not be preferred over (or substitute for) the backend's
      // status-derived approved_by.
      approved_by: breakRequest?.approved_by,
      approved_at: breakRequest?.approved_at || breakRequest?.approved_on,
      rejected_by: breakRequest?.rejected_by,
      rejected_at: breakRequest?.rejected_at || breakRequest?.rejected_on,
      cancelled_by: breakRequest?.cancelled_by || breakRequest?.modified_by,
      cancelled_at:
        breakRequest?.cancelled_at ||
        breakRequest?.cancelled_on ||
        (breakRequest?.status === "Cancelled" ? breakRequest?.modified_at : undefined),
      cancelled_reason:
        breakRequest?.cancelled_reason ||
        (breakRequest?.status === "Cancelled"
          ? breakRequest?.rejected_reason
          : undefined),
    }),
    [breakRequest],
  );

  if (!breakRequest) return null;

  const status = canonicalBreakStatus(breakRequest?.status);
  const isPending = status === "Pending";
  // Requested per user: allow cancelling a break request even after it has
  // been Approved (not just while Pending), same as Comp Off.
  const canCancelApproved = status === "Approved";
  const employeeName = breakRequest?.employee_name || "N/A";
  const employeeId = breakRequest?.employee_id || breakRequest?.emp_id || "-";
  const department =
    breakRequest?.department_name || breakRequest?.department || "-";
  const designation =
    breakRequest?.designation_name || breakRequest?.designation || "-";
  const rawAttendanceType =
    breakRequest?.raw_attendance_type || breakRequest?.attendance_type;
  const isCancelBreak = rawAttendanceType === "Cancel Break";
  const requestReason = breakRequest?.reason || breakRequest?.description || "-";
  const rejectedReason =
    status === "Rejected" ? breakRequest?.rejected_reason || "" : "";
  const cancelledReason =
    breakRequest?.cancelled_reason ||
    (status === "Cancelled" ? breakRequest?.rejected_reason || "" : "");

  const breakStartValue =
    breakRequest?.raw_break_start_time || breakRequest?.break_start_time;
  const breakEndValue =
    breakRequest?.raw_break_end_time || breakRequest?.break_end_time;
  const breakStartDisplay = breakStartValue
    ? dayjs(breakStartValue).format("HH:mm")
    : "-";
  const breakEndDisplay = breakEndValue
    ? dayjs(breakEndValue).format("HH:mm")
    : "-";
  const breakDurationDisplay = breakRequest?.duration
    ? breakRequest.duration
    : breakStartValue && breakEndValue
      ? `${dayjs(breakEndValue).diff(
          dayjs(breakStartValue),
          "minute",
        )} minutes`
      : "-";
  const breakCategoryRaw =
    breakRequest?.raw_break_type || breakRequest?.break_type;
  // Was a plain "Shift_break" ? "Shift Break" : "OT Break" ternary — any
  // unrecognised/missing value (e.g. a Cancel Break row's deduction-type
  // break_type, which is neither of these two enum strings) silently fell
  // into the "OT Break" branch, showing that even for actual Shift Break
  // requests.
  const breakCategoryDisplay =
    breakCategoryRaw === "Shift_break"
      ? "Shift Break"
      : breakCategoryRaw === "Ot_break" || breakCategoryRaw === "OT_break"
        ? "OT Break"
        : "-";
  const cancelBreakRows = Array.isArray(breakRequest?.breaks)
    ? breakRequest.breaks.map((item, index) => ({
        id: item?.break_id ?? item?.id ?? index,
        startTime: item?.break_start_time || item?.start_time,
        endTime: item?.break_end_time || item?.end_time,
        duration: item?.duration || "-",
      }))
    : [];

  const handleCloseAction = () => {
    onClose?.();
  };

  const deriveBreakIds = () => {
    if (
      Array.isArray(breakRequest?.break_ids) &&
      breakRequest.break_ids.length
    ) {
      return breakRequest.break_ids.filter(Boolean);
    }
    if (
      Array.isArray(breakRequest?.breaks_to_cancel) &&
      breakRequest.breaks_to_cancel.length
    ) {
      return breakRequest.breaks_to_cancel
        .map((entry) => entry?.break_id)
        .filter(Boolean);
    }
    if (Array.isArray(breakRequest?.breaks) && breakRequest.breaks.length) {
      return breakRequest.breaks
        .map((entry) => entry?.break_id ?? entry?.id)
        .filter(Boolean);
    }
    if (breakRequest?.break_id) {
      return [breakRequest.break_id];
    }
    return [];
  };

  const updateBreakStatus = async (requestStatus, reason) => {
    const payload = isCancelBreak
      ? {
          id: breakRequest.id,
          user_id: breakRequest.user_id,
          attendance_type: "Cancel Break",
          reason: breakRequest?.reason,
          status: requestStatus,
          break_ids: deriveBreakIds(),
          ...(requestStatus === "Rejected" && { rejected_reason: reason }),
          ...(requestStatus === "Cancelled" && { cancelled_reason: reason }),
        }
      : {
          id: breakRequest.id,
          user_id: breakRequest.user_id,
          attendance_type: "break",
          reason: breakRequest?.reason,
          status: requestStatus,
          break_type: breakRequest?.raw_break_type || breakRequest?.break_type,
          break_start_time: breakStartValue,
          break_end_time: breakEndValue,
          duration: breakRequest?.duration,
          date: toISODate(breakRequest?.date),
          ...(requestStatus === "Rejected" && { rejected_reason: reason }),
          ...(requestStatus === "Cancelled" && { cancelled_reason: reason }),
        };

    const method =
      !isCancelBreak &&
      requestStatus === "Rejected" &&
      breakRequest?.status === "Approved"
        ? "PATCH"
        : "PUT";

    const response = await customAPI(
      `${domain}team_zone/approve/work_adjustment/actual-special-attendance/?company_id=${requestCompanyId}`,
      method,
      payload,
    );
    if (response?.ok) {
      showToast(
        requestStatus === "Rejected" ? "error" : "success",
        response?.responseDetails?.success?.[0]?.message ||
          `Request ${requestStatus.toLowerCase()}.`,
      );
      setReCallApi?.(true);
      setRejectDialogOpen(false);
      setCancelDialogOpen(false);
      onClose?.();
      return true;
    }
    showToast(
      "error",
      response?.message ||
        `Couldn't ${
          requestStatus === "Approved"
            ? "approve"
            : requestStatus === "Cancelled"
              ? "cancel"
              : "reject"
        } the request. Please try again.`,
    );
    return false;
  };

  const handleApprove = async () => {
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to approve this request",
      );
      return;
    }
    setApproving(true);
    await updateBreakStatus("Approved");
    setApproving(false);
  };

  const submitReject = async (data) => {
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to reject this request",
      );
      return;
    }
    setRejecting(true);
    await updateBreakStatus("Rejected", data?.rejected_reason);
    setRejecting(false);
  };

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    reset({ rejected_reason: "" });
    clearErrors("rejected_reason");
  };

  const submitCancel = async () => {
    const trimmed = cancelReason.trim();
    if (!trimmed) {
      setCancelReasonError("Reason for cancellation is required");
      return;
    }
    if (trimmed.length < 3) {
      setCancelReasonError(minimum3Characters);
      return;
    }
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to cancel this request",
      );
      return;
    }
    setCancelling(true);
    await updateBreakStatus("Cancelled", trimmed);
    setCancelling(false);
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelReason("");
    setCancelReasonError("");
  };

  // Reject/Approve/Cancel below used to always render, only disabled (via
  // AccessGatedAction) when hasApproverAccess was false — leaving a
  // bordered footer strip with unusable buttons for an editor-only viewer.
  // Each branch now resolves to real content or a genuine `null` (same fix
  // as OverTime/DetailPanel.js) so GlobalSidePanel's own footer border only
  // draws when there's actually something to show.
  const footerContent = isPending ? (
    hasApproverAccess ? (
      <Mui.Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 1.5,
        }}
      >
        <Mui.Button
          variant="outlined"
          startIcon={<HighlightOffIcon />}
          onClick={() => setRejectDialogOpen(true)}
          sx={{
            ...designShell.panelDangerButton,
            color: "#B42318",
            borderColor: "#F5C2C7",
            backgroundColor: "#FEF3F2",
            "&:hover": {
              borderColor: "#E59CA5",
              backgroundColor: "#FEE4E2",
            },
          }}
        >
          Reject
        </Mui.Button>
        <PrimaryButton
          startIcon={<CheckCircleOutlineIcon />}
          loading={approving}
          onClick={handleApprove}
        >
          Approve
        </PrimaryButton>
      </Mui.Box>
    ) : null
  ) : canCancelApproved ? (
    hasApproverAccess ? (
      <Mui.Stack spacing={1.5}>
        <Mui.Typography
          sx={{ ...approvePanelUi.panelMutedText, textAlign: "center" }}
        >
          This request has already been approved.
        </Mui.Typography>
        <RequestCancelButton onClick={() => setCancelDialogOpen(true)}>
          Cancel Break
        </RequestCancelButton>
      </Mui.Stack>
    ) : null
  ) : (
    <Mui.Typography
      sx={{ ...approvePanelUi.panelMutedText, textAlign: "center" }}
    >
      This request has already been {status?.toLowerCase() || "processed"}.
    </Mui.Typography>
  );

  const rejectDialog = (
    <GlobalConfirmDialog
      open={rejectDialogOpen}
      title="Reject Break Request"
      onCancel={closeRejectDialog}
      onConfirm={() => {
        if (!watch("rejected_reason")?.trim()) {
          setError("rejected_reason", { type: "required", message: "Reject reason is required" });
          return;
        }
        handleSubmit(submitReject)();
      }}
      confirmLabel="Reject"
      cancelLabel="Cancel"
      loading={rejecting}
      danger
      reasonValue={watch("rejected_reason") || ""}
      onReasonChange={(value) => setValue("rejected_reason", value, { shouldValidate: true })}
      reasonError={errors?.rejected_reason?.message || ""}
      reasonPlaceholder="Provide a reason for rejection"
      reasonMinRows={2}
      maxWidth="sm"
    >
      <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
        Enter the reason for rejecting this Break request.
      </Mui.Typography>
    </GlobalConfirmDialog>
  );

  const cancelDialog = (
    <GlobalConfirmDialog
      open={cancelDialogOpen}
      title="Cancel Break Request"
      onCancel={closeCancelDialog}
      onConfirm={submitCancel}
      confirmLabel="Continue"
      cancelLabel="Keep"
      loading={cancelling}
      loadingLabel="Cancelling..."
      danger
      reasonValue={cancelReason}
      onReasonChange={(value) => {
        setCancelReason(value);
        if (cancelReasonError) setCancelReasonError("");
      }}
      reasonError={cancelReasonError}
      reasonPlaceholder="Provide a reason for cancelling this approved break"
      reasonMinRows={2}
      maxWidth="sm"
    >
      <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
        This break request has already been approved. Enter a reason to
        cancel it.
      </Mui.Typography>
    </GlobalConfirmDialog>
  );

  return (
    <>
      <GlobalSidePanel
        open={open}
        onClose={handleCloseAction}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        title={isCancelBreak ? "Cancel Break Request" : "Apply Break Request"}
        subtitle="Request details and status"
        headerSx={{ px: 2.5, pt: 2.5, pb: 1.5 }}
        PaperProps={{
          sx: {
            backgroundColor: designColors.white,
            maxWidth: "92vw",
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            overflow: "hidden",
          },
        }}
        breadcrumb={[
          { label: "Approval Center", onClick: goToApprovalCenter },
          { label: "Break", onClick: handleCloseAction },
          employeeName || "Break Request",
        ]}
        footer={footerContent}
      >
        <Mui.Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <RequestDetailHeader
            userDetails={{
              image_url: breakRequest?.image_url,
              employee_id: employeeId,
              user_name: employeeName,
              department_name: department,
              designation,
            }}
            status={status}
            colorCode={breakRequest?.color_code || breakStatusVisual(status).accent}
          />

          <Mui.Box sx={detailCardSx}>
            <Mui.Typography sx={{ ...detailSectionLabelSx, mb: 1.75 }}>
              Request Details
            </Mui.Typography>
            {isCancelBreak ? (
              <>
                <RequestDetailField
                  icon={CalendarMonthIcon}
                  label="Date"
                  value={breakRequest?.date || "-"}
                  accent={detailAccent.date}
                />
                <Mui.Box sx={{ pt: 0.5 }}>
                  <Mui.Typography sx={{ ...detailSectionLabelSx, mb: 1 }}>
                    Break Rows
                  </Mui.Typography>
                  <Mui.TableContainer
                    sx={{
                      border: `1px solid ${designColors.slate200}`,
                      borderRadius: 2,
                    }}
                  >
                    <Mui.Table
                      size="small"
                      sx={{
                        "& .MuiTableCell-root": {
                          fontFamily: "inherit",
                          borderColor: designColors.slate200,
                        },
                        "& .MuiTableHead-root .MuiTableCell-root": {
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: "0.03em",
                          color: designColors.slate500,
                          backgroundColor: designColors.slate50 || "#F8FAFC",
                        },
                        "& .MuiTableBody-root .MuiTableCell-root": {
                          fontSize: 13.5,
                          color: designColors.slate900,
                        },
                      }}
                    >
                      <Mui.TableHead>
                        <Mui.TableRow>
                          <Mui.TableCell>Start Time</Mui.TableCell>
                          <Mui.TableCell>End Time</Mui.TableCell>
                          <Mui.TableCell>Duration</Mui.TableCell>
                        </Mui.TableRow>
                      </Mui.TableHead>
                      <Mui.TableBody>
                        {cancelBreakRows.length ? (
                          cancelBreakRows.map((item) => (
                            <Mui.TableRow key={item.id}>
                              <Mui.TableCell>
                                {formatBreakTableTime(item.startTime)}
                              </Mui.TableCell>
                              <Mui.TableCell>
                                {formatBreakTableTime(item.endTime)}
                              </Mui.TableCell>
                              <Mui.TableCell>{item.duration}</Mui.TableCell>
                            </Mui.TableRow>
                          ))
                        ) : (
                          <Mui.TableRow>
                            <Mui.TableCell colSpan={3} align="center">
                              No break details available
                            </Mui.TableCell>
                          </Mui.TableRow>
                        )}
                      </Mui.TableBody>
                    </Mui.Table>
                  </Mui.TableContainer>
                </Mui.Box>
              </>
            ) : (
              <>
                <RequestDetailField
                  icon={CategoryOutlinedIcon}
                  label="Category"
                  value={breakCategoryDisplay}
                  accent={detailAccent.category}
                />
                <RequestDetailField
                  icon={CalendarMonthIcon}
                  label="Date"
                  value={breakRequest?.date || "-"}
                  accent={detailAccent.date}
                />
                <RequestDetailField
                  icon={AccessTimeIcon}
                  label="Start Time"
                  value={breakStartDisplay}
                  accent={detailAccent.startTime}
                />
                <RequestDetailField
                  icon={AccessTimeIcon}
                  label="End Time"
                  value={breakEndDisplay}
                  accent={detailAccent.endTime}
                />
                <RequestDetailField
                  icon={TimerOutlinedIcon}
                  label="Duration"
                  value={breakDurationDisplay}
                  accent={detailAccent.duration}
                />
              </>
            )}
            <RequestDetailField
              icon={DescriptionOutlinedIcon}
              label="Reason"
              value={requestReason}
              accent={detailAccent.reason}
              multiline
              isLast={!rejectedReason && !cancelledReason}
            />
            {rejectedReason ? (
              <RequestDetailField
                icon={DescriptionOutlinedIcon}
                label="Reject Reason"
                value={rejectedReason}
                accent={detailAccent.rejectReason}
                multiline
                isLast
              />
            ) : null}
            {cancelledReason ? (
              <RequestDetailField
                icon={DescriptionOutlinedIcon}
                label="Cancel Reason"
                value={cancelledReason}
                accent={detailAccent.rejectReason}
                multiline
                isLast
              />
            ) : null}
          </Mui.Box>

          <RequestStatusTimeline
            endDate={breakRequest?.date}
            data={statusFlowData}
            variant="stepper"
            hidePendingStep
          />
          <ApprovalEditAuditTrail data={breakRequest} />
        </Mui.Box>
      </GlobalSidePanel>
      {rejectDialog}
      {cancelDialog}
    </>
  );
};
